import type { Article, ArticleModule } from 'types/capi/article';

import { getModulePositions } from '~/utils/modulePositions';
import { isMobileViewport } from '~/utils/moduleVisibility';

export type ArticleModuleManifestEntry = {
  module_position: number;
  module_type: string;
};

export type ArticleViewProperties = {
  article_id: string;
  modules: ArticleModuleManifestEntry[];
};

// The render manifest: every CMS module the article rendered with a box on
// this viewport, in render order, with the same module_position the module
// impressions carry — so the furthest module a reader saw can be read against
// how many there were. The entries are exactly the modules getModulePositions
// numbered, so the ordinals here are a contiguous run from 1 to the manifest's
// length: rows are containers and take no number, a module that renders
// nothing takes none, and on a mobile viewport a module a row hides on mobile
// takes none, because it is max-md:hidden there and can never be seen. The
// hardcoded sites around the article (header, related articles, recent
// features) sit in no numbered sequence and are not part of the manifest
// either.
export function getArticleModuleManifest(
  modules: ArticleModule[],
  { mobile = false }: { mobile?: boolean } = {}
): ArticleModuleManifestEntry[] {
  return Array.from(
    getModulePositions(modules, { mobile }),
    ([module, modulePosition]) => ({
      module_position: modulePosition,
      module_type: module.type
    })
  );
}

// A v1 article is prose with no modules, so its manifest is honestly empty
// rather than absent: the contract requires the array from schema_version 2.
// The viewport is read at build time, so the manifest says what this reader's
// screen rendered, the same screen the impressions fire from.
export default function getArticleViewProperties(
  article: Article,
  mobile = isMobileViewport()
): ArticleViewProperties {
  return {
    article_id: article.id,
    modules:
      article.template === 'v2'
        ? getArticleModuleManifest(article.modules, { mobile })
        : []
  };
}
